<template>
  <div class="change-log-list">
    <div class="change-log-header">
      <div class="section-title">修改记录</div>
      <el-button size="mini" icon="el-icon-refresh" :disabled="!openid" @click="loadLogs">刷新</el-button>
    </div>

    <el-table
      v-if="logs.length"
      :data="logs"
      size="mini"
      border
      v-loading="loading"
      class="change-log-table"
    >
      <el-table-column prop="changedAt" label="修改时间" width="150" />
      <el-table-column prop="operator" label="操作人" width="100" />
      <el-table-column prop="changedFieldLabels" label="变更字段" min-width="140" />
      <el-table-column label="变更详情" min-width="260">
        <template slot-scope="{ row }">
          <div v-if="parseChangeDetails(row).length" class="change-detail-lines">
            <div
              v-for="item in parseChangeDetails(row)"
              :key="`${row.id}-${item.field}`"
              class="change-detail-line"
              :class="{ 'image-change-detail': isImageChange(item) }"
            >
              <template v-if="isImageChange(item)">
                <div class="change-field image-change-title">{{ item.label || item.field }}</div>
                <div class="image-change-content">
                  <div class="image-change-group">
                    <div class="image-group-title">修改前</div>
                    <div v-if="item.oldImages.length" class="change-images">
                      <el-image
                        v-for="(url, index) in item.oldImages"
                        :key="`old-${index}-${url}`"
                        :src="url"
                        :preview-src-list="item.oldImages"
                        fit="cover"
                        class="change-image"
                      >
                        <div slot="error" class="image-error">加载失败</div>
                      </el-image>
                    </div>
                    <span v-else class="change-value">-</span>
                  </div>
                  <span class="change-arrow image-change-arrow">→</span>
                  <div class="image-change-group">
                    <div class="image-group-title">修改后</div>
                    <div v-if="item.newImages.length" class="change-images">
                      <el-image
                        v-for="(url, index) in item.newImages"
                        :key="`new-${index}-${url}`"
                        :src="url"
                        :preview-src-list="item.newImages"
                        fit="cover"
                        class="change-image"
                      >
                        <div slot="error" class="image-error">加载失败</div>
                      </el-image>
                    </div>
                    <span v-else class="change-value">-</span>
                  </div>
                </div>
              </template>
              <template v-else>
                <span class="change-field">{{ item.label || item.field }}</span>
                <span class="change-value">{{ formatChangeValue(item.oldValue) }}</span>
                <span class="change-arrow">→</span>
                <span class="change-value">{{ formatChangeValue(item.newValue) }}</span>
              </template>
            </div>
          </div>
          <span v-else>-</span>
        </template>
      </el-table-column>
    </el-table>

    <el-empty
      v-else
      v-loading="loading"
      description="暂无修改记录"
      :image-size="72"
      class="change-log-empty"
    />
  </div>
</template>

<script>
import { fetchUserProfileChangeLogs } from '../api/admin'
import { isAuthRedirectError } from '../utils/auth'

export default {
  name: 'UserProfileChangeLogList',
  props: {
    openid: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      logs: []
    }
  },
  watch: {
    openid: {
      immediate: true,
      handler(value) {
        if (value) {
          this.loadLogs()
        } else {
          this.logs = []
        }
      }
    }
  },
  methods: {
    async loadLogs() {
      if (!this.openid) return
      this.loading = true
      try {
        this.logs = await fetchUserProfileChangeLogs(this.openid)
      } catch (error) {
        if (isAuthRedirectError(error)) return
        this.$message.error(error.message || '修改记录获取失败')
      } finally {
        this.loading = false
      }
    },
    parseChangeDetails(row) {
      const raw = row && row.changeDetailJson
      if (!raw) return []
      try {
        const parsed = JSON.parse(raw)
        if (!Array.isArray(parsed)) return []
        return parsed.map(item => {
          const detail = item || {}
          if (!this.isImageChange(detail)) return detail
          return {
            ...detail,
            oldImages: this.imageList(detail.oldValue),
            newImages: this.imageList(detail.newValue)
          }
        })
      } catch (error) {
        return []
      }
    },
    formatChangeValue(value) {
      const text = String(value || '').trim()
      return text || '-'
    },
    isImageChange(item) {
      return item && item.field === 'images'
    },
    imageList(value) {
      if (Array.isArray(value)) {
        return value.map(item => String(item || '').trim()).filter(Boolean)
      }
      const text = String(value || '').trim()
      if (!text) return []
      return text
        .split(/[,，;；\r\n]+/)
        .map(item => item.trim())
        .filter(Boolean)
    }
  }
}
</script>

<style scoped>
.change-log-list {
  margin-top: 20px;
}

.change-log-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
}

.change-log-table {
  width: 100%;
}

.change-detail-lines {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.change-detail-line {
  line-height: 1.5;
  word-break: break-all;
}

.change-field {
  margin-right: 8px;
  color: #334155;
  font-weight: 600;
}

.change-value {
  color: #64748b;
}

.change-arrow {
  margin: 0 6px;
  color: #94a3b8;
}

.image-change-detail {
  display: block;
}

.image-change-title {
  display: block;
  margin-bottom: 6px;
}

.image-change-content {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
}

.image-change-group {
  min-width: 70px;
}

.image-group-title {
  margin-bottom: 4px;
  color: #64748b;
  font-size: 12px;
}

.change-images {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.change-image {
  width: 54px;
  height: 54px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #f8fafc;
}

.image-error {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  color: #94a3b8;
  font-size: 12px;
  background: #f8fafc;
}

.image-change-arrow {
  margin-top: 22px;
}

.change-log-empty {
  padding: 10px 0;
}
</style>
